import React, {
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  AlertModal,
  Toast,
  Favicon,
  GoogleRecaptchaProvider,
  ErrorBoundary,
  Loading,
} from 'components';
import { I18nextProvider } from 'react-i18next';
import { Router } from 'react-router-dom';

import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { datadogRum } from '@datadog/browser-rum';

import { useAuth0 } from '@auth0/auth0-react';
import { useCookies } from 'react-cookie';
import jwtDecode from 'jwt-decode';
import LocationProvider from 'lane-shared/components/LocationProvider';
import RendererProvider from 'lane-shared/components/RendererProvider';
import { WHITELABEL_LANE } from 'lane-shared/config/whitelabels';
import { emitAuthEventForAuth0 } from 'lane-shared/helpers/auth0/emitAuthEventForAuth0';
import emitter, {
  EVENT_AUTH_TOKEN_INVALID,
  EVENT_AUTH_SESSION_RECOVER,
} from 'lane-shared/helpers/emitter';
import setSharedCookiesForWebView from './helpers/setSharedCookiesForWebView';
import {
  AppContext,
  UserDataContext,
  ChannelsContext,
  AnalyticsContext,
} from 'lane-shared/contexts';
import SignUpContextProvider from 'lane-shared/contexts/SignUpContext/SignUpContextProvider';
import { AuthUserContextProvider } from 'lane-shared/contexts/AuthUserContext/AuthUserContextProvider';
import {
  useAppData,
  useUserData,
  useChannelFromSlug,
  useFlag,
} from 'lane-shared/hooks';
import { useUserChannelsData } from 'react-hooks';
import { useAuth0Enabled } from 'lane-shared/hooks/auth0/useAuth0Enabled';
import { useAuth0ConsolidateAuthTokenStorage } from 'lane-shared/hooks/auth0/useAuth0ConsolidateAuthTokenStorage';
import i18n from 'localization';
import { PlatformEnum } from 'constants-activate';
import { WhiteLabelType } from 'lane-shared/types/WhiteLabelType';
import { useFonts } from './hooks/useFonts';
import {
  useAuth0SessionRecovery,
  isUnrenewableSessionError,
  hasStoredAuth0Session,
} from './hooks/useAuth0SessionRecovery';

import BlockingLoad from 'components/general/BlockingLoad';

import { createMuiTheme } from 'static/materialUITheme';

import * as primitives from './components/renderers/v5/primitives';
import locationHelper from './helpers/Location';
import history from './helpers/history';
import { FeatureFlag } from 'constants-flags';
import { getLinkHandler } from './helpers/linkHandler';
import './helpers/setAppHeight';
import { useAnonymousChannelsData } from './hooks';

import { getWhitelabelFonts } from 'lane-shared/config/whitelabels/whitelabelFonts';

import { ThemeController } from './ThemeController';
import { ThemeBlockingLoad } from 'components/general/ThemeBlockingLoad';
import { useGetAuthToken } from './hooks/useGetAuthToken';
import { IntercomChatProvider } from './components/integrations/Intercom/IntercomChatProvider';

// Transient token-renewal failures (proxy/CORS 403, network blips, timeouts)
// are retried with linear backoff instead of logging the user out. One retry
// is enough of a blip guard before escalating to recovery: a dead/rotated
// refresh token often masquerades as `timeout` (iframe fallback blocked), so
// escalating after a single retry (~3s) instead of two heals the common case
// faster while still absorbing a one-off network hiccup.
const TRANSIENT_RENEWAL_RETRY_LIMIT = 1;
const TRANSIENT_RENEWAL_RETRY_BASE_MS = 3000;

export default function Providers({ children, withRouter = true }: any) {
  const urlSearchParams = new URLSearchParams(document.location.search);
  const jti = urlSearchParams.get('tokenid') ?? undefined;
  const jwt = urlSearchParams.get('token') ?? undefined;
  // A targeted channel/content in the URL signals a deep-link session —
  // home channel routing must not override the intended destination.
  const isDeepLink = Boolean(
    urlSearchParams.get('channelId') ||
    urlSearchParams.get('contentId')
  );
  
  const getAuthToken = useGetAuthToken();
  const isAuth0Enabled = useAuth0Enabled();
  const isConsolidateStorageOn = useAuth0ConsolidateAuthTokenStorage();
  const skipStorageWrite = isAuth0Enabled && isConsolidateStorageOn;

  const {
    user,
    loading,
    error,
    route,
    hasAttemptedLogin,
    isLoggingIn,
    isLoggedIn,
    isInitialized,
    refetch,
    hasAnyPermission,
    sessionId,
    sessionInvalidatedByMeError,
    meQuerySucceeded,
    defaultChannelId,
  } = useUserData({ jti, jwt, getAuthToken, skipStorageWrite });

  const {
    isAuthenticated: isAuthenticatedViaAuth0,
    getAccessTokenSilently,
    isLoading: isAuth0Loading,
  } = useAuth0();
  const { recoverSession, markSessionRecovered } = useAuth0SessionRecovery();
  const [, setCookie] = useCookies(['jti', 'token']);
  const didEmitForSessionRef = useRef(false);
  const recoveryStartedRef = useRef(false);
  const [isRecoveringSession, setIsRecoveringSession] = useState(false);

  // Claims the recovery guard, holds the loader, and runs recoverSession()
  // with both non-navigating outcomes handled:
  //  - resolved WITHOUT navigating (no stored session to recover — the only
  //    no-op path): release, so the router can show Login instead of a spinner.
  //  - rejected (e.g. the fallback clearSession/logout teardown threw before
  //    any navigation): release, otherwise the loader and the one-shot guard
  //    stay set forever and the user is trapped with recovery blocked.
  // On the normal path loginWithRedirect resolves right after
  // location.assign(), while the page is unloading — the loader must then STAY
  // up (releasing would flash Login mid-redirect), which is why release is
  // conditional on there being no stored session rather than unconditional.
  const startSessionRecovery = () => {
    recoveryStartedRef.current = true;
    setIsRecoveringSession(true);

    recoverSession()
      .then(() => {
        if (!hasStoredAuth0Session()) {
          setIsRecoveringSession(false);
          recoveryStartedRef.current = false;
        }
      })
      .catch(() => {
        setIsRecoveringSession(false);
        recoveryStartedRef.current = false;
      });
  };

  // The shared channels/focus queries emit EVENT_AUTH_SESSION_RECOVER
  // on a "not logged in" (mobile heals it via dead-RT recovery). On web,
  // getAccessTokenSilently already auto-refreshes tokens, so preserve the
  // legacy behavior — treat it as a token invalidation.
  useEffect(() => {
    const onRecover = () => emitter.emit(EVENT_AUTH_TOKEN_INVALID);

    emitter.addListener(EVENT_AUTH_SESSION_RECOVER, onRecover);

    return () => {
      emitter.removeListener(EVENT_AUTH_SESSION_RECOVER, onRecover);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticatedViaAuth0) {
      didEmitForSessionRef.current = false;

      return;
    }

    if (didEmitForSessionRef.current) {
      return;
    }

    didEmitForSessionRef.current = true;

    async function fetchAuth0Token(attempt = 0) {
      try {
        const auth0Token = await getAccessTokenSilently({
          detailedResponse: true,
          // Non-consolidate keeps the original 15s (behaviour untouched). Under
          // the consolidate flag use a short cap: a healthy refresh-token
          // exchange resolves well under this, and the only thing needing the
          // long leash is the silent-auth iframe fallback — exactly the slow,
          // often-blocked path we want to bail out of quickly and recover via a
          // top-level redirect instead of waiting ~15s.
          timeoutInSeconds: isConsolidateStorageOn ? 5 : 15,
        });

        // Token renewed successfully — clear any pending recovery attempt.
        markSessionRecovered();

        await emitAuthEventForAuth0(auth0Token.id_token);

        const { activate_session_id } = jwtDecode<{
          activate_session_id: string;
        }>(auth0Token.id_token);

        try {
          await setSharedCookiesForWebView(
            auth0Token.id_token,
            activate_session_id,
            setCookie
          );
        } catch (cookieError) {
          datadogRum.addError(cookieError, {
            component: 'Providers',
            action: 'setSharedCookiesForWebView',
          });
        }
      } catch (error) {
        datadogRum.addError(error, {
          component: 'Providers',
          action: 'fetchAuth0Token',
        });

        // Session recovery is gated on the consolidate-auth-token-storage flag.
        // With the flag off, keep the legacy behavior — don't recover or retry.
        if (!isConsolidateStorageOn) {
          didEmitForSessionRef.current = false;

          return;
        }

        // A dead refresh token surfaces two ways: a clean unrenewable code
        // (invalid_grant / login_required), or — with useRefreshTokensFallback
        // on — masked as `timeout`: the token endpoint's 403 is swallowed and
        // the SDK falls back to the silent-auth iframe, which never posts back
        // when third-party cookies are blocked and times out. For BOTH,
        // retrying just burns another full getAccessTokenSilently timeout on
        // the identical failure, so recover immediately when a stored session
        // exists. recoverSession()'s loginWithRedirect is a top-level
        // navigation that bypasses the blocked iframe and resolves fast: SSO
        // renew (stays logged in) or login_required → clean logout.
        const looksLikeMaskedDeadRefreshToken =
          (error as { error?: string })?.error === 'timeout';

        if (
          isUnrenewableSessionError(error) ||
          (hasStoredAuth0Session() && looksLikeMaskedDeadRefreshToken)
        ) {
          if (!recoveryStartedRef.current) {
            startSessionRecovery();
          }

          return;
        }

        // Genuinely transient failure (proxy 5xx/403, network drop): retry with
        // backoff first — a healthy session usually renews on the next attempt
        // without the visible recovery redirect.
        if (attempt < TRANSIENT_RENEWAL_RETRY_LIMIT) {
          await new Promise(resolve =>
            setTimeout(resolve, TRANSIENT_RENEWAL_RETRY_BASE_MS * (attempt + 1))
          );

          return fetchAuth0Token(attempt + 1);
        }

        // Retries exhausted. With a stored session this is persistent, not a
        // blip — escalate to recovery so the session still heals (SSO renew)
        // or exits cleanly, instead of stranding a logged-in user tokenless.
        if (hasStoredAuth0Session()) {
          if (!recoveryStartedRef.current) {
            startSessionRecovery();
          }

          return;
        }

        didEmitForSessionRef.current = false;
      }
    }

    fetchAuth0Token();
    // Triggered only by auth state changes; the effect is one-shot per session
    // (guarded by didEmitForSessionRef), so the recovery callbacks are left out
    // of the deps on purpose.
  }, [isAuthenticatedViaAuth0, getAccessTokenSilently, isConsolidateStorageOn]);

  // TS-41719: watchdog for the Auth0Provider's initial checkSession() stalling
  // on a dead refresh token. When the refresh 403s, the SDK falls back to the
  // silent-auth iframe and — with third-party cookies blocked — waits out its
  // full ~60s timeout before isAuthenticatedViaAuth0 ever flips, so the
  // mount-effect recovery above can't start and the user stares at a loader.
  // If we are still loading after this window AND a stored Auth0 session exists,
  // the session is stuck: skip the wait and recover now via the top-level
  // redirect, which bypasses the blocked iframe. Consolidate-gated; a healthy
  // checkSession resolves well under this so it never fires for good sessions.
  const AUTH0_LOADING_STALL_MS = 5000;

  useEffect(() => {
    if (
      !isConsolidateStorageOn ||
      !isAuth0Loading ||
      recoveryStartedRef.current ||
      !hasStoredAuth0Session()
    ) {
      return;
    }

    const timer = setTimeout(() => {
      if (recoveryStartedRef.current || !hasStoredAuth0Session()) {
        return;
      }

      startSessionRecovery();
    }, AUTH0_LOADING_STALL_MS);

    return () => clearTimeout(timer);
    // startSessionRecovery is recreated per render; intentionally not in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConsolidateStorageOn, isAuth0Loading]);

  const getHost = (url: string) => {
    // https://*.sub.domain.name.com to match sub.domain.name.com
    const pattern = /^(https?:\/\/)?(\*\.)?(?<host>[^/:]+)(:\d+)?(\/.*)?$/;

    return pattern.exec(url)?.groups?.host?.toLowerCase();
  };

  const address = window.location.href;
  const addressHost = getHost(address);
  const analytics = useContext(AnalyticsContext);
  const enableAnalyticContentTouchPhone = useFlag(
    FeatureFlag.SquadTechnicalSolutionsEnableAnalyticContentTouchPhone,
    false
  );

  const isIntercomProviderEnabled = useFlag(
    FeatureFlag.SquadTechnicalSolutionsEnableIntercomIntegration,
    false
  );

  const linkHandler = useMemo(
    () => getLinkHandler(analytics, user?._id, enableAnalyticContentTouchPhone),
    [analytics, user?._id, enableAnalyticContentTouchPhone]
  );

  const wrappedChildren = isIntercomProviderEnabled ? (
    <IntercomChatProvider>{children}</IntercomChatProvider>
  ) : (
    children
  );

  function getChannelSlug() {
    const pathNameSplit = window.location.pathname.split('/');

    return pathNameSplit[4] === 'admin' ? pathNameSplit[3] : null;
  }

  const channelSlug = getChannelSlug();
  const { channel: adminFocusChannel } = useChannelFromSlug({
    channelSlug: channelSlug ?? null,
  });

  const {
    whitelabel,
    whitelabels,
    hasWhiteLabelLoaded,
    cards,
    blocks,
    isTransitioning,
    transition,
    clearTransition,
    isBlockingLoad,
    setIsBlockingLoad,
    isThemeBlockingLoad,
    setIsThemeBlockingLoad,
  } = useAppData({
    defaultInstance: WHITELABEL_LANE,
    findWhiteLabel: (whitelabel: WhiteLabelType) => {
      const isHostSame = (wlHost: string) =>
        addressHost && getHost(wlHost)?.toLowerCase() === addressHost;

      return whitelabel.hosts.some(isHostSame);
    },
  });

  const { bodyFont, headerFont } = getWhitelabelFonts(whitelabel?.instance);

  const MuiTheme = useMemo(
    () => createMuiTheme(bodyFont.fontFamily),
    [bodyFont.fontFamily]
  );

  const channelsData = useUserChannelsData({
    user,
    channelId: adminFocusChannel?._id,
    isDeepLink,
    defaultChannelId,
  });

  const anonChannelsData = useAnonymousChannelsData({
    user,
    isInitialized,
    isLoggingIn,
  });

  useEffect(() => {
    if (
      !isLoggedIn &&
      whitelabel?.locale &&
      i18n.language !== whitelabel.locale
    ) {
      i18n.changeLanguage(whitelabel.locale);
    }
  }, [whitelabel?.locale, isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn && user?.profile) {
      datadogRum.setGlobalContext({
        user: {
          _id: user.profile?._id || '',
        },
      });
    }
  }, [user, isLoggedIn]);

  useFonts({ bodyFont, headerFont });

  // https://reactjs.org/docs/refs-and-the-dom.html#caveats-with-callback-refs
  function setAlertRef(ref: AlertModal) {
    window.Alert = ref;
  }

  // https://reactjs.org/docs/refs-and-the-dom.html#caveats-with-callback-refs
  function setToastRef(ref: Toast) {
    window.Toast = ref;
  }

  let channelForTheme =
    anonChannelsData?.primaryChannel ?? anonChannelsData?.secondaryChannel;

  if (user) {
    channelForTheme =
      channelsData?.primaryChannel ?? channelsData?.secondaryChannel;
  }

  // TS-41719: under the consolidate flag, hold a loader (never the Welcome/Login
  // screen) for the entire dead-RT window:
  //  - isAuth0Loading: the Auth0Provider's initial checkSession() is still in
  //    flight (a dead RT drags this out via the silent-auth iframe timeout),
  //    isAuthenticatedViaAuth0 is briefly false — without this the router shows
  //    Login for that whole window.
  //  - isAuthenticatedViaAuth0 && !isLoggedIn: SDK says authenticated but the
  //    app hasn't logged in yet — token acquisition / recovery in progress.
  //  - isRecoveringSession: recoverSession()'s redirect is firing.
  // Confirmed-invalid sessions (sessionInvalidatedByMeError) fall through to
  // Login. !meQuerySucceeded excludes the SSO-signup / new-user case: there the
  // token is valid and the me query succeeds (session but no user yet, routing
  // to onboarding), so the user is legitimately authenticated-but-not-logged-in
  // and must NOT be trapped behind the recovery loader. In real recovery the me
  // query never runs (broken token), so meQuerySucceeded stays false.
  // Entirely gated on the consolidate flag → FF-off is untouched.
  const showRecoverySessionLoader =
    isConsolidateStorageOn &&
    (isRecoveringSession ||
      // Only hold the loader through the SDK init window when there is a stored
      // session to recover. A never-logged-in visitor has no @@auth0spajs@@
      // entry, so hasStoredAuth0Session() is false and the router renders the
      // login page immediately instead of a blank loader during checkSession().
      (isAuth0Loading && hasStoredAuth0Session()) ||
      (isAuthenticatedViaAuth0 &&
        !isLoggedIn &&
        !sessionInvalidatedByMeError &&
        !meQuerySucceeded));

  const routedChildren = withRouter ? (
    <Router history={history}>{wrappedChildren}</Router>
  ) : (
    wrappedChildren
  );

  return (
    <Suspense fallback={<Loading />}>
      {/* @ts-expect-error ts-migrate(2322) FIXME: Type 'UserType | null' is not assignable to type '... Remove this comment to see the full error message */}
      <ErrorBoundary user={user} sessionId={sessionId}>
        <I18nextProvider i18n={i18n}>
          <RendererProvider
            platform={PlatformEnum.Web}
            primitives={primitives}
            linkHandler={linkHandler}
            blockDefinitions={blocks}
          >
            <LocationProvider locationHelper={locationHelper}>
              <AppContext.Provider
                value={{
                  // @ts-expect-error ts-migrate(2322) FIXME: Type 'Partial<WhiteLabelType>' is not assignable t... Remove this comment to see the full error message
                  whitelabel,
                  whitelabels,
                  cards,
                  blocks,
                  transition,
                  hasWhiteLabelLoaded,
                  isTransitioning,
                  isBlockingLoad,
                  setIsBlockingLoad,
                  clearTransition,
                  isThemeBlockingLoad,
                  setIsThemeBlockingLoad,
                }}
              >
                <AuthUserContextProvider>
                  <UserDataContext.Provider
                    value={{
                      user,
                      error,
                      route,
                      hasAttemptedLogin,
                      isLoggingIn,
                      loading,
                      isLoggedIn,
                      isInitialized,
                      sessionInvalidatedByMeError,
                      meQuerySucceeded,
                      // @ts-expect-error ts-migrate(2322) FIXME: Type '(variables?: Partial<OperationVariables> | u... Remove this comment to see the full error message
                      refetch,
                      hasAnyPermission,
                    }}
                  >
                    <GoogleRecaptchaProvider>
                      <MuiThemeProvider theme={MuiTheme}>
                        <ChannelsContext.Provider
                          // @ts-expect-error ts-migrate(2322) FIXME: Type 'ChannelsContextType | { readonly channels: r... Remove this comment to see the full error message
                          value={user ? channelsData : anonChannelsData}
                        >
                          <ThemeController
                            channel={channelForTheme}
                            bodyFont={bodyFont}
                            headerFont={headerFont}
                          >
                            <SignUpContextProvider>
                              <Favicon />
                              <BlockingLoad />
                              <ThemeBlockingLoad />
                              {showRecoverySessionLoader ? (
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '100%',
                                    height: '100vh',
                                  }}
                                >
                                  <Loading />
                                </div>
                              ) : (
                                routedChildren
                              )}
                            </SignUpContextProvider>
                          </ThemeController>
                        </ChannelsContext.Provider>
                      </MuiThemeProvider>
                      <AlertModal ref={setAlertRef} />
                      <Toast ref={setToastRef} />
                    </GoogleRecaptchaProvider>
                  </UserDataContext.Provider>
                </AuthUserContextProvider>
              </AppContext.Provider>
            </LocationProvider>
          </RendererProvider>
        </I18nextProvider>
      </ErrorBoundary>
    </Suspense>
  );
}
